// client/src/api/ApiAvailability.js
import flex from './FlexFetch'

const availability_endpoint = '/api/1.0/availability/'

// Helper function to standardize error handling
const handleApiError = (error, defaultMessage) => {
  const rawError = error.response?.data?.error
  const errorMessage =
    (typeof rawError === 'string' ? rawError : rawError?.message) ||
    error.message ||
    defaultMessage
  throw new Error(errorMessage)
}

async function create(availabilityData) {
  try {
    const response = await flex.flexFetch(availability_endpoint + 'post', {
      method: 'POST',
      body: JSON.stringify(availabilityData),
      credentials: 'include',
    })
    return await flex.handleResponse(response)
  } catch (error) {
    handleApiError(error, 'Failed to create availability')
  }
}

async function getMyAvailability(league_id = null, division_id = null) {
  try {
    // Build query parameters
    const params = new URLSearchParams()
    if (league_id) params.append('league_id', league_id)
    if (division_id) params.append('division_id', division_id)

    const queryString = params.toString()
    const url =
      availability_endpoint + 'get' + (queryString ? `?${queryString}` : '')

    const response = await flex.flexFetch(url, {
      credentials: 'include',
    })
    return await flex.handleResponse(response)
  } catch (error) {
    handleApiError(error, 'Failed to fetch availability')
  }
}

async function deleteAvailability(id) {
  try {
    const response = await flex.flexFetch(availability_endpoint + 'delete', {
      method: 'DELETE',
      body: JSON.stringify({ id }),
      credentials: 'include',
    })
    return await flex.handleResponse(response)
  } catch (error) {
    handleApiError(error, 'Failed to delete availability')
  }
}

async function setTeamAvailability(availabilityData) {
  try {
    const response = await flex.flexFetch(availability_endpoint + 'team/post', {
      method: 'POST',
      body: JSON.stringify(availabilityData),
      credentials: 'include',
    })
    return await flex.handleResponse(response)
  } catch (error) {
    handleApiError(error, 'Failed to create team availability')
  }
}

async function updateTeamAvailability(sharedTeamSlotId, availabilityData) {
  try {
    const response = await flex.flexFetch(
      availability_endpoint + 'team/update',
      {
        method: 'PUT',
        body: JSON.stringify({
          ...availabilityData,
          shared_team_slot_id: sharedTeamSlotId,
        }),
        credentials: 'include',
      },
    )
    return await flex.handleResponse(response)
  } catch (error) {
    handleApiError(error, 'Failed to update team availability')
  }
}

async function deleteTeamAvailability(sharedTeamSlotId) {
  try {
    const response = await flex.flexFetch(
      availability_endpoint + 'team/delete',
      {
        method: 'DELETE',
        body: JSON.stringify({ shared_team_slot_id: sharedTeamSlotId }),
        credentials: 'include',
      },
    )
    return await flex.handleResponse(response)
  } catch (error) {
    handleApiError(error, 'Failed to delete team availability')
  }
}

async function addTeamException(sharedTeamSlotId, exceptionDate) {
  try {
    const response = await flex.flexFetch(
      availability_endpoint + 'team/addException',
      {
        method: 'POST',
        body: JSON.stringify({
          shared_team_slot_id: sharedTeamSlotId,
          exception_date: exceptionDate,
        }),
        credentials: 'include',
      },
    )
    return await flex.handleResponse(response)
  } catch (error) {
    handleApiError(error, 'Failed to add team availability exception')
  }
}

async function deleteTeamDay({ team_id, league_id, division_id, matchDate }) {
  try {
    const response = await flex.flexFetch(
      availability_endpoint + 'team/deleteDay',
      {
        method: 'DELETE',
        body: JSON.stringify({ team_id, league_id, division_id, matchDate }),
        credentials: 'include',
      },
    )
    return await flex.handleResponse(response)
  } catch (error) {
    handleApiError(error, 'Failed to delete team availability for day')
  }
}

async function addException(id, exceptionDate) {
  try {
    const response = await flex.flexFetch(
      availability_endpoint + 'addException',
      {
        method: 'POST',
        body: JSON.stringify({ id, exceptionDate }),
        credentials: 'include',
      },
    )
    return await flex.handleResponse(response)
  } catch (error) {
    handleApiError(error, 'Failed to add exception to recurring availability')
  }
}

async function getOverlaps(league_id, division_id) {
  try {
    let url = availability_endpoint + `overlaps?league_id=${league_id}`
    if (division_id) {
      url += `&division_id=${division_id}`
    }

    const response = await flex.flexFetch(url, {
      credentials: 'include',
    })
    return await flex.handleResponse(response)
  } catch (error) {
    handleApiError(error, 'Failed to fetch availability overlaps')
  }
}

async function getLeagueAvailability(league_id, division_id) {
  try {
    let url =
      availability_endpoint + `leagueAvailability?league_id=${league_id}`
    if (division_id) {
      url += `&division_id=${division_id}`
    }

    const response = await flex.flexFetch(url, {
      credentials: 'include',
    })
    return await flex.handleResponse(response)
  } catch (error) {
    handleApiError(error, 'Failed to fetch league availability')
  }
}

async function updateAvailability(availabilityData) {
  try {
    const response = await flex.flexFetch(availability_endpoint + 'update', {
      method: 'PUT',
      body: JSON.stringify(availabilityData),
      credentials: 'include',
    })
    return await flex.handleResponse(response)
  } catch (error) {
    handleApiError(error, 'Failed to update availability')
  }
}

const ApiAvailability = {
  create,
  getMyAvailability,
  deleteAvailability,
  setTeamAvailability,
  updateTeamAvailability,
  deleteTeamAvailability,
  addTeamException,
  deleteTeamDay,
  addException,
  getOverlaps,
  getLeagueAvailability,
  updateAvailability,
}

export default ApiAvailability
